import { RadioTabGroup, RadioTabGroupProps } from '@florencelabs/radio-tab';
import { useLayoutEffect, useRef, FC, Ref, RefObject } from 'react';

/**
 * Wrapper around `@florencelabs/radio-tab` RadioTabGroup that enforces the WAI-ARIA
 * APG roving tabindex pattern.
 *
 * This is the same fix as `RovingFocusRadioGroup` but for the published
 * RadioTabGroup component. See `roving-focus-radio-group.tsx` for full
 * problem description and rationale.
 *
 * ## Why a separate wrapper?
 * RadioTabGroup wraps RAC's RadioGroup internally with its own props
 * (options, errorMessage, etc.), so we can't reuse `RovingFocusRadioGroup`
 * directly. The tabindex-patching logic is the same — both query the DOM
 * for radio inputs and fix their tabIndex after RAC renders.
 *
 * ## Removal
 * Remove alongside `RovingFocusRadioGroup` once the upstream RAC bug is fixed.
 */
export interface RovingFocusRadioTabGroupProps extends RadioTabGroupProps {
  ref?: Ref<HTMLDivElement>;
}

export const RovingFocusRadioTabGroup: FC<RovingFocusRadioTabGroupProps> = ({
  ref,
  ...props
}) => {
  const internalRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = internalRef.current;
    if (!container) return;

    const radios = container.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]',
    );
    if (radios.length === 0) return;

    // When a radio is checked, RAC correctly assigns tabIndex=0 to the
    // checked radio and -1 to the rest — no patching needed.
    const hasChecked = Array.from(radios).some((r) => r.checked);
    if (hasChecked) return;

    // Nothing is checked — enforce roving tabindex: first enabled radio
    // gets tabIndex=0 (reachable via Tab), all others get -1 (reachable
    // only via arrow keys). Guards avoid redundant setAttribute calls
    // when the tabIndex is already correct.
    let firstEnabled: HTMLInputElement | undefined;
    radios.forEach((radio) => {
      if (!radio.disabled && !firstEnabled) {
        firstEnabled = radio;
        if (radio.tabIndex !== 0) {
          radio.setAttribute('tabindex', '0');
        }
      } else {
        if (radio.tabIndex !== -1) {
          radio.setAttribute('tabindex', '-1');
        }
      }
    });
  });

  return (
    <RadioTabGroup
      ref={(node) => {
        // Assign to internal ref for useLayoutEffect
        internalRef.current = node;
        // Forward ref to caller (ref is properly typed as HTMLDivElement in the published package)
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as RefObject<HTMLDivElement | null>).current = node;
        }
      }}
      {...props}
    />
  );
};
